import {ColourMode, Common} from '@taopix/taopix-core';
import {AssetMetaInterface, AssetService} from '@taopix/taopix-core/dist/types/types';

export type ExternalAssetProps = {
  proxysrc: string;
  src: string;
  height: number;
  width: number;
};

export type ExternalAssetHires = ExternalAssetProps & {
  colourmode: ColourMode;
  filesizebytes: number;
  filedatemodified: number;
  filetype: number;
};

export type ExternalAssetMeta = Partial<AssetMetaInterface> & {
  hires: ExternalAssetHires;
  fpo: ExternalAssetProps;
  thumb: ExternalAssetProps;
};

export type CanvasImage = Pick<AssetMetaInterface, 'rotation' | 'height' | 'width'> & {
  src: string;
  processed: boolean;
  img: HTMLImageElement;
};

type Dimension = {
  width: number;
  height: number;
};

const NAMED_EXTERNAL_SERVICES = ['%FACEBOOK%', '%FLICKR%', '%GOOGLEPHOTOS%', '%INSTAGRAM%'];

export const isLibraryLink = (assetservice: AssetService): boolean => {
  return (
    !NAMED_EXTERNAL_SERVICES.includes(assetservice.code) &&
    true === Boolean(assetservice.customservice)
  );
};

// asset processed status
export const STATUS_PROCESSED = 6;

// are browsers rotating images
const ORIENTATION_FROM_IMAGE = getComputedStyle(document.body)['imageOrientation'] === 'from-image';

export const hasExpired = (expirationdate: string) => {
  if (expirationdate !== '0000-00-00 00:00:00' && expirationdate !== '') {
    const mySQLDateArray: Array<string> = expirationdate.split(/[- :]/);
    const expirationDate = new Date(
      parseInt(mySQLDateArray[0]),
      parseInt(mySQLDateArray[1]) - 1,
      parseInt(mySQLDateArray[2]),
      parseInt(mySQLDateArray[3]),
      parseInt(mySQLDateArray[4]),
      parseInt(mySQLDateArray[5])
    );
    const expirationDateSecond = expirationDate.getTime() / 1000;

    const theDate = new Date();
    const browserEpochSeconds = Math.round(theDate.getTime() / 1000);

    if (expirationDateSecond - browserEpochSeconds < 0) {
      return true;
    }
  }
  return false;
};

/**
 * Flips the input size based on the rotaiton and if the imageOrientation style being set to from-image.
 *
 * @param   {number}          rotation
 * @param   {ImageDimension}  size
 *
 * @return  {ImageDimension}
 */
export const flipSize = (rotation: number, size: Dimension): Dimension => {
  /**
   * If the image orientation is set to from-image then we need to cope with the image
   * being a different rotation and some of the  dimensions being rotated.
   */
  if (ORIENTATION_FROM_IMAGE && [90, 270].includes(rotation)) {
    return {
      width: size.height,
      height: size.width,
    };
  }

  return size;
};

/**
 * Figure out the rotation to reverse the rotation applied by the imageOrigination style.
 *
 * @param   {number}  rotation
 *
 * @return  {number}
 */
export const getRotation = (rotation: number): number => {
  // If the browser is automatically rotating the image then reverse the rotation.
  if (ORIENTATION_FROM_IMAGE) {
    switch (rotation) {
      case 0:
      case 180: {
        // No need to switch the rotation around
        return rotation;
      }
      case 90: {
        return 270;
      }
      case 270: {
        return 90;
      }
    }
  }
  return 0;
};

/**
 * Calculate the rotation of an asset
 *
 * @param   {AssetMetaInterface}  asset
 *
 * @return  {number}
 */
export const calcRotation = (asset: AssetMetaInterface): number => {
  if ('undefined' === typeof asset?.status) {
    const externalRotation = asset?.externalRotation ?? 0;
    // if the image isn't processed and it's not in error and it has an external rotation
    // calculate the rotation based on the images external rotation minus it's current rotation minus.
    if (asset.status !== 6 && asset.status !== 8 && externalRotation !== 0) {
      return (360 + (externalRotation - asset.rotation)) % 360;
    } else {
      return getRotation(asset.rotation);
    }
  } else {
    return getRotation(asset.rotation);
  }
};

/**
 * Return the scaled height/width based off a maximum threshold
 *
 * @param   {number}          width
 * @param   {number}          height
 * @param   {number}          max
 * @param   {boolean}         reverse
 *
 * @return  {ImageDimension}
 */
export const getScaledSize = (
  width: number,
  height: number,
  max: number,
  reverse = false
): Dimension => {
  let scale = 0;

  if (reverse) {
    scale = Math.min(width / max, height / max);
  } else {
    scale = Math.max(width / max, height / max);
  }

  return {
    width: Math.round(width / scale),
    height: Math.round(height / scale),
  };
};

/**
 * Apply an alpha channel/mask to an image
 *
 * @param   {ImageData}  imageData  [pImageData description]
 * @param   {ImageData}  maskData   [maskData description]
 *
 * @return  {ImageData}              [return description]
 */
const applyAlpha = (imageData: ImageData, maskData: ImageData): ImageData => {
  const imageObjectLength = imageData.data.length;
  const imageObjectData = imageData.data;
  const totalPixels = imageObjectLength / 4;

  let backgroundPixelValue = 255;
  let darkPixelCount = 0;
  let lightPixelCount = 0;

  // determine if we should composite the image onto a black background or a white background
  for (let i = 0; i < imageObjectLength; i += 4) {
    // convert the pixel value into a greyscale value
    const g =
      imageObjectData[i] * 0.33 + imageObjectData[i + 1] * 0.5 + imageObjectData[i + 2] * 0.16;

    // convert the mask pixel value into an alpha value
    const a = (255 - maskData.data[i]) / 255;

    // composite the greyscale value onto a white background
    const out = 255 * (1 - a) + g * a;

    // values <= 128 are dark pixels and values >= 225 are light pixels
    if (out <= 128) {
      darkPixelCount++;
    } else if (out >= 225) {
      lightPixelCount++;
    }
  }

  // if we have more than 1% dark pixels then state that we should composite this image onto a white background
  // otherwise if we have more than 95% light pixels then state that we should composite this image onto a black background
  if ((darkPixelCount / totalPixels) * 100 > 1) {
    backgroundPixelValue = 255;
  } else if ((lightPixelCount / totalPixels) * 100 > 95) {
    backgroundPixelValue = 0;
  }

  // composite the image onto the required background colour
  for (let i = 0; i < imageObjectLength; i += 4) {
    // convert the mask pixel value into an alpha value
    const a = (255 - maskData.data[i]) / 255;

    // precalculate the alpha blended background value as we will use it multiple times
    const b = backgroundPixelValue * (1 - a);

    // blend the image with its alpha channel onto the background
    const outR = b + imageObjectData[i] * a;
    const outG = b + imageObjectData[i + 1] * a;
    const outB = b + imageObjectData[i + 2] * a;

    // store the new pixel values with an opaque alpha channel
    imageObjectData[i] = outR;
    imageObjectData[i + 1] = outG;
    imageObjectData[i + 2] = outB;
    imageObjectData[i + 3] = 255;
  }

  // return a new image data object containing the pixels
  return new ImageData(imageObjectData, imageData.width, imageData.height);
};

/**
 * Draw an image & optional alpha/mask to a canvas
 *
 * @return  {HTMLCanvasElement}
 */
export const drawImageToCanvas = (
  canvasImageData: CanvasImage,
  canvas: HTMLCanvasElement | null = null,
  merge = false
): HTMLCanvasElement => {
  if (null === canvas) {
    canvas = document.createElement('canvas');
  }

  const rotation: number = (360 - canvasImageData.rotation) % 360;
  const ctx = canvas.getContext('2d', {willReadFrequently: true}) as CanvasRenderingContext2D;
  const imageData: ImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  if (merge) {
    ctx.save();
  }

  const rotate: boolean = [90, 270].includes(rotation);

  // switch the canvas dimensions based on the rotation value
  if (rotate) {
    canvas.width = canvasImageData.height;
    canvas.height = canvasImageData.width;
  } else {
    canvas.width = canvasImageData.width;
    canvas.height = canvasImageData.height;
  }

  if (rotate && canvasImageData.processed) {
    ctx.translate(canvasImageData.height / 2, canvasImageData.width / 2);
  } else {
    ctx.translate(canvasImageData.width / 2, canvasImageData.height / 2);
  }

  if (canvasImageData.processed) {
    ctx.rotate(Common.degreesToRadians(rotation));
  }

  ctx.imageSmoothingEnabled = false;
  ctx.globalCompositeOperation = 'copy';

  // if the asset has been processed, scale to the scaled image size, otherwise scale to the canvas size
  const scaleDimensions: Dimension = canvasImageData.processed
    ? {height: canvasImageData.height, width: canvasImageData.width}
    : {width: canvas.width, height: canvas.height};

  // draw the image
  ctx.drawImage(
    canvasImageData.img,
    -canvasImageData.width / 2,
    -canvasImageData.height / 2,
    scaleDimensions.width,
    scaleDimensions.height
  );

  if (merge) {
    ctx.restore();

    const mergedImageData = ctx.getImageData(0, 0, scaleDimensions.width, scaleDimensions.height);
    const finalImageData: ImageData = applyAlpha(imageData, mergedImageData);

    ctx.putImageData(finalImageData, 0, 0);
  }

  return canvas;
};

/**
 * Converts a data URL string to a blob
 *
 * @param   {string}  dataUrl
 *
 * @return  {Blob}
 */
export const dataURItoBlob = (dataUrl: string): Blob => {
  const byteString = atob(dataUrl.split(',')[1]);
  const byteStringLength = byteString.length;

  const ab = new ArrayBuffer(byteStringLength);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteStringLength; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], {type: 'image/jpeg'});
};
